import React, { useEffect, useMemo, useRef, useState } from "react";
import NewPartnerFields, { NEW_PARTNER_REASON, emptyNewPartner, isNewPartnerComplete, needsSituationChoice } from "../../family/NewPartnerFields";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiHash,
  FiClipboard,
  FiHome,
  FiMail,
  FiEdit2,
  FiShield,
  FiMessageSquare,
  FiMinusCircle,
  FiMoreHorizontal,
  FiPlus,
  FiSave,
  FiUser,
  FiUsers,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import {
  PRIVACY_CHANNELS,
  PRIVACY_VISIBILITY_OPTIONS,
  DEFAULT_VISIBILITY,
  buildInitialChannels,
} from "../../../../features/privacy/definitions";
import TeacherContactCard from "../../family/components/desktop/TeacherContactCard";
import ChildAddSection from "../../family/components/desktop/ChildAddSection";
import ChildHealthProfilePanel from "../../family/components/ChildHealthProfilePanel";
import FamilyHealthProfileView from "../../family/components/desktop/FamilyHealthProfileView";
import ClassParentsSection from "../../../../components/ClassParentsSection";
import ClassLinksSection from "../../../../components/ClassLinksSection";
import {
  CHILD_OVERVIEW_STATUS,
  hasConfiguredPrivacy,
  resolveChildOverviewStatus,
} from "../../utils/childOverviewStatus";



const SlideIn = ({ children, direction = "right" }) => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  const offset = direction === "left" ? "-18px" : "18px";

  return (
    <div
      style={{
        opacity: entered ? 1 : 0,
        transform: entered ? "translateX(0)" : `translateX(${offset})`,
        transition: "opacity 220ms ease-out, transform 260ms cubic-bezier(0.22, 1, 0.36, 1)",
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
};

const DEFAULT_CHANNEL_LABELS = {
  website: "Allow on website",
  newsletter: "Allow in newsletter",
  socials: "Allow on social media",
  promotionalMaterials: "Allow in promotional materials",
  internalPublications: "Allow in internal publications",
  localMedia: "Allow in local media",
  videoRecordings: "Allow video recordings",
};

const DEFAULT_VISIBILITY_LABELS = {
  VISIBLE: "Visible",
  NOT_VISIBLE: "Not visible",
  UNSET: "No preference set",
};

const resolveVisibilityLabel = (value, t) => {
  const normalized = value || DEFAULT_VISIBILITY;
  const option = PRIVACY_VISIBILITY_OPTIONS.find(({ value: opt }) => opt === normalized);
  const fallback = DEFAULT_VISIBILITY_LABELS[normalized] || normalized;
  if (!option?.labelKey) return fallback;
  return t(option.labelKey, { defaultValue: fallback });
};

const PrivacyStatusPill = ({ value, t }) => {
  const ok = !!value;
  return (
    <span
      className={`inline-flex min-w-14 items-center justify-center rounded-full px-3 py-1 text-xs font-bold ${
        ok
          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
          : "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200"
      }`}
    >
      {ok ? t("common.yes", { defaultValue: "Yes" }) : t("common.no", { defaultValue: "No" })}
    </span>
  );
};

const hasLoadedStateData = (state) => Boolean(state && Object.prototype.hasOwnProperty.call(state, "data"));

const EmptyPrivacyState = ({ t, onSetPrivacy }) => (
  <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5 dark:border-emerald-900/60 dark:bg-emerald-950/20">
    <div className="flex gap-4">
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-emerald-700 shadow-sm dark:bg-slate-950 dark:text-emerald-300">
        <FiShield className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <h4 className="text-base font-bold text-slate-950 dark:text-white">
          {t("privacy.empty.title", { defaultValue: "No privacy settings yet" })}
        </h4>
        <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
          {t("privacy.empty.description", {
            defaultValue:
              "Privacy and publication preferences have not been set for this child yet. Set them now so the school knows what is allowed.",
          })}
        </p>
        {typeof onSetPrivacy === "function" && (
          <button
            type="button"
            onClick={onSetPrivacy}
            className="mt-4 inline-flex w-full items-center justify-center rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-emerald-700 sm:w-auto"
          >
            {t("privacy.set_settings", { defaultValue: "Set privacy settings" })}
          </button>
        )}
      </div>
    </div>
  </div>
);

const PendingPersonalUpdateNotice = ({ t }) => (
  <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
    <div className="flex gap-3">
      <FiAlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <div>
        <h4 className="text-sm font-bold">
          {t("parentPortal.child_info.pending_change", { defaultValue: "Pending change" })}
        </h4>
        <p className="mt-1 text-sm leading-6">
          {t("parentPortal.child_info.pending_change_hint", {
            defaultValue:
              "A change to these personal details is waiting for approval. Editing is temporarily unavailable until this request has been processed.",
          })}
        </p>
      </div>
    </div>
  </div>
);

const InterestListNotice = ({ t, variant = "profile" }) => {
  const title =
    variant === "class"
      ? t("parentPortal.children.class_disabled_waiting_title", { defaultValue: "Class information is not available yet" })
      : variant === "privacy"
        ? t("parentPortal.children.privacy_disabled_waiting_title", { defaultValue: "Privacy settings are not needed yet" })
        : t("parentPortal.children.waiting_profile_title", { defaultValue: "Child is still on the waiting list" });

  const description =
    variant === "class"
      ? t("parentPortal.children.class_disabled_waiting_description", {
          defaultValue: "This child has not been placed in a class yet. Class and teacher details become available after placement.",
        })
      : variant === "privacy"
        ? t("parentPortal.children.privacy_disabled_waiting_description", {
            defaultValue: "Privacy and publication settings can be set after the child has been placed.",
          })
        : t("parentPortal.children.waiting_profile_description", {
            defaultValue: "Only limited profile information is available while this child is still on the waiting list.",
          });

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-5 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
      <div className="flex gap-3">
        <FiAlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div>
          <h4 className="text-sm font-bold">{title}</h4>
          <p className="mt-1 text-sm leading-6">{description}</p>
        </div>
      </div>
    </div>
  );
};

const PrivacyEditor = ({ t, initial, disabled, onCancel, onSave }) => {
  const [channels, setChannels] = useState(() => buildInitialChannels(initial));
  const [visibility, setVisibility] = useState(initial?.visibility ?? DEFAULT_VISIBILITY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const hasExistingPrivacy = !!initial && typeof initial === "object";

  useEffect(() => {
    setChannels(buildInitialChannels(initial));
    setVisibility(initial?.visibility ?? DEFAULT_VISIBILITY);
  }, [initial]);

  const dirty =
    !hasExistingPrivacy ||
    PRIVACY_CHANNELS.some(({ key }) => !!channels[key] !== !!initial?.[key]) ||
    (initial?.visibility ?? DEFAULT_VISIBILITY) !== visibility;

  const handleSave = async () => {
    if (!dirty || disabled || saving) return;
    setError(null);
    setSaving(true);

    try {
      const payload = PRIVACY_CHANNELS.reduce(
        (acc, { key }) => ({ ...acc, [key]: !!channels[key] }),
        {},
      );
      await onSave?.({
        ...payload,
        visibility: visibility === "UNSET" ? null : visibility,
        ...(initial?.id ? { id: initial.id } : {}),
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("privacy.save_error", { defaultValue: "Failed to save changes" }),
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (key) => {
    if (disabled || saving) return;
    setChannels((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-2xl border border-slate-100 dark:border-slate-800">
        <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <h4 className="text-sm font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
            {t("privacy.channels", { defaultValue: "Publication channels" })}
          </h4>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {PRIVACY_CHANNELS.map(({ key, labelKey }) => {
            const checked = !!channels[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleChannel(key)}
                disabled={disabled || saving}
                aria-pressed={checked}
                className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-slate-900/60"
              >
                <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                  {t(labelKey, { defaultValue: DEFAULT_CHANNEL_LABELS[key] || key })}
                </span>
                <span
                  className={`inline-flex min-w-16 items-center justify-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${
                    checked
                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                      : "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200"
                  }`}
                >
                  {checked ? <FiCheckCircle className="h-3.5 w-3.5" aria-hidden="true" /> : <FiX className="h-3.5 w-3.5" aria-hidden="true" />}
                  {checked ? t("common.yes", { defaultValue: "Yes" }) : t("common.no", { defaultValue: "No" })}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-100 dark:border-slate-800">
        <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <h4 className="text-sm font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
            {t("privacy.visibility.title", { defaultValue: "Visibility" })}
          </h4>
        </div>
        <div className="grid gap-2 p-3 sm:grid-cols-3">
          {PRIVACY_VISIBILITY_OPTIONS.map(({ value, labelKey }) => {
            const selected = visibility === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setVisibility(value)}
                disabled={disabled || saving}
                aria-pressed={selected}
                className={`rounded-2xl border px-3 py-3 text-center text-sm font-bold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                  selected
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200"
                    : "border-slate-100 bg-white text-slate-600 hover:border-emerald-200 hover:bg-emerald-50/50 dark:border-slate-800 dark:bg-slate-950/20 dark:text-slate-300 dark:hover:bg-emerald-950/20"
                }`}
              >
                {t(labelKey, { defaultValue: DEFAULT_VISIBILITY_LABELS[value] || value })}
              </button>
            );
          })}
        </div>
      </div>

      {error && <p className="text-sm font-semibold text-red-700 dark:text-red-300">{error}</p>}

      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-base font-bold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          {t("common.cancel", { defaultValue: "Cancel" })}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={disabled || saving || !dirty}
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-base font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FiSave aria-hidden="true" />
          {saving ? t("common.saving", { defaultValue: "Saving..." }) : t("common.save_changes", { defaultValue: "Save changes" })}
        </button>
      </div>
    </div>
  );
};

const FAMILY_SITUATION_REASONS = [
  { value: "NEW_PARTNER", labelKey: "parentPortal.familySituation.reasons.newPartner", defaultValue: "New partner" },
  { value: "SINGLE_PARENT", labelKey: "parentPortal.familySituation.reasons.singleParent", defaultValue: "Single parent" },
  { value: "CHILD_OTHER_FAMILY", labelKey: "parentPortal.familySituation.reasons.childOtherFamily", defaultValue: "Child belongs to another family" },
  { value: "INCORRECT_LINK", labelKey: "parentPortal.familySituation.reasons.incorrectLink", defaultValue: "Incorrect link" },
  { value: "OTHER", labelKey: "parentPortal.familySituation.reasons.other", defaultValue: "Other" },
];

const FAMILY_SITUATIONS = [
  "MARRIED",
  "COHABITING",
  "SEPARATED",
  "DIVORCED",
  "SINGLE_PARENT",
  "WIDOWED",
  "OTHER",
];

const FAMILY_SITUATION_DEFAULTS = {
  MARRIED: "Married",
  COHABITING: "Cohabiting",
  SEPARATED: "Separated",
  DIVORCED: "Divorced",
  SINGLE_PARENT: "Single parent",
  WIDOWED: "Widowed",
  OTHER: "Other",
};

const familySituationLabel = (t, value) =>
  t(`parentPortal.familySituation.options.${value || "MARRIED"}`, {
    defaultValue: FAMILY_SITUATION_DEFAULTS[value] || FAMILY_SITUATION_DEFAULTS.MARRIED,
  });

const fullName = (...parts) => parts.filter(Boolean).join(" ").trim();

const getChildName = (child) =>
  fullName(child?.firstName, child?.lastName) || child?.displayName || child?.name || "-";

const getChildKey = (child) => child?.id || child?.uuid;
const getChildApiId = (child) => child?.id;

const getChildSubtitle = (child, t) =>
  child?.className ||
  child?.classroomName ||
  child?.classroom?.className ||
  child?.classroom?.name ||
  t("parentPortal.children.profile", { defaultValue: "Child profile" });

const initialsFor = (name) => String(name || "?").trim().slice(0, 1).toUpperCase() || "?";

const formatDateValue = (value) => {
  if (!value) return "—";

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value.map(Number);
    if (year && month && day) {
      return `${String(day).padStart(2, "0")}-${String(month).padStart(2, "0")}-${year}`;
    }
  }

  const raw = String(value).trim();
  if (!raw) return "—";

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw.split("-").reverse().join("-");
  }

  const commaDate = raw.match(/^(\d{4})[,/](\d{1,2})[,/](\d{1,2})$/);
  if (commaDate) {
    const [, year, month, day] = commaDate;
    return `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
  }

  if (/^\d{7,8}$/.test(raw)) {
    const year = raw.slice(0, 4);
    const rest = raw.slice(4);
    const month = rest.length === 3 ? rest.slice(0, 1) : rest.slice(0, 2);
    const day = rest.length === 3 ? rest.slice(1) : rest.slice(2);
    return `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
  }

  return raw;
};



const formatDateTimeValue = (value) => {
  if (value === undefined || value === null || value === "") return "—";

  const raw = String(value).trim();
  if (!raw) return "—";

  let date = null;

  if (value instanceof Date) {
    date = value;
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    date = new Date(`${raw}T00:00:00`);
  } else if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    date = new Date(raw);
  } else if (/^\d{13}$/.test(raw)) {
    date = new Date(Number(raw));
  } else if (/^\d{10}(?:\.\d+)?$/.test(raw)) {
    date = new Date(Number(raw) * 1000);
  } else if (/^\d{7,8}$/.test(raw)) {
    const formatted = formatDateValue(raw);
    const [day, month, year] = formatted.split("-");
    date = new Date(`${year}-${month}-${day}T00:00:00`);
  } else {
    date = new Date(raw);
  }

  if (!date || Number.isNaN(date.getTime())) return "—";

  // updatedAt/lastModifiedAt should never parse to a Unix-epoch fallback date in this UI.
  if (date.getFullYear() < 2000) return "—";

  const storedLocale = typeof window !== "undefined" ? window.localStorage?.getItem("i18nextLng") : "";
  const locale = storedLocale?.startsWith("nl")
    ? "nl-NL"
    : storedLocale?.startsWith("en")
      ? "en-US"
      : typeof navigator !== "undefined" && navigator.language
        ? navigator.language
        : "en-US";
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const startOfDay = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const getAssignmentStartDate = (assignment) =>
  assignment?.startDate ??
  assignment?.classroom?.startDate ??
  assignment?.schoolYear?.startDate ??
  assignment?.classroom?.schoolYear?.startDate;

const getAssignmentEndDate = (assignment) =>
  assignment?.endDate ??
  assignment?.classroom?.endDate ??
  assignment?.schoolYear?.endDate ??
  assignment?.classroom?.schoolYear?.endDate;

const isActiveClassroomAssignment = (assignment) => {
  if (!assignment) return false;
  if (assignment.isCurrent === true || assignment.current === true) return true;
  if (assignment.isCurrent === false || assignment.current === false) return false;

  const today = startOfDay(new Date());
  const startDate = startOfDay(getAssignmentStartDate(assignment));
  const endDate = startOfDay(getAssignmentEndDate(assignment));

  if (startDate && today < startDate) return false;
  if (endDate && today > endDate) return false;
  if (startDate || endDate) return true;

  return true;
};

const getClassroomAssignments = (child, classroomsByChild) => {
  const childKey = getChildKey(child);
  const loaded = childKey ? classroomsByChild?.[childKey]?.data : null;
  const source = firstValue(
    loaded,
    child?.classroomAssignments,
    child?.classrooms,
    child?.assignments,
    child?.classroomAssignment,
  );
  return Array.isArray(source) ? source : source ? [source] : [];
};

const getActiveClassroomAssignment = (child, classroomsByChild) => {
  const assignments = getClassroomAssignments(child, classroomsByChild);
  return assignments.find(isActiveClassroomAssignment) || assignments[0] || null;
};

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");

const yesNoUnknown = (value, t) => {
  if (value === true) return t("parentPortal.yes", { defaultValue: "Yes" });
  if (value === false) return t("parentPortal.no", { defaultValue: "No" });
  return t("parentPortal.children.not_available", { defaultValue: "Not available" });
};

const humanizePrivacyKey = (key) =>
  String(key || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const getChildPrivacyEntries = (child, t) => {
  if (!child || typeof child !== "object") return [];

  const privacyTerms = ["privacy", "publication", "photo", "image", "consent", "permission"];

  return Object.entries(child)
    .filter(([key, value]) => {
      if (value === undefined || value === null || value === "") return false;
      if (typeof value === "object") return false;
      const normalizedKey = key.toLowerCase();
      return privacyTerms.some((term) => normalizedKey.includes(term));
    })
    .map(([key, value]) => ({
      key,
      label: humanizePrivacyKey(key),
      value: typeof value === "boolean" ? yesNoUnknown(value, t) : String(value),
    }));
};

const formatStatus = (value, t) => {
  if (value === true) return t("parentPortal.children.status_active", { defaultValue: "Active" });
  if (value === false) return t("parentPortal.children.status_inactive", { defaultValue: "Inactive" });
  if (value === undefined || value === null || String(value).trim() === "") return "—";
  return String(value);
};

const isWaitingListChild = (child) => {
  if (!child) return false;
  if (child.waiting === true) return true;

  const values = [child.status, child.placementStatus, child.registrationStatus, child.enrollmentStatus]
    .filter((value) => value !== undefined && value !== null)
    .map((value) => String(value).trim().toLowerCase());

  return values.some((value) =>
    ["waiting", "waitlist", "waiting_list", "interest", "interest_list", "interesse", "interesselijst"].includes(value),
  );
};

const getChildStatus = (child, t) => {
  if (isWaitingListChild(child)) {
    return t("parentPortal.waiting", { defaultValue: "Waiting list" });
  }

  return formatStatus(firstValue(child?.status, child?.placementStatus, child?.registrationStatus, child?.enrollmentStatus, child?.committed), t);
};

const childOverviewStatusMeta = (status, t) => {
  const entries = {
    [CHILD_OVERVIEW_STATUS.COMMITTED]: {
      label: t("parentPortal.children.overview_status.committed", { defaultValue: "Committed" }),
      description: t("parentPortal.children.overview_status.committed_hint", { defaultValue: "Child will attend the school" }),
      Icon: FiCheckCircle,
      tone: "bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/50",
    },
    [CHILD_OVERVIEW_STATUS.NO_CLASS]: {
      label: t("parentPortal.children.overview_status.no_class", { defaultValue: "No class yet" }),
      description: t("parentPortal.children.overview_status.no_class_hint", { defaultValue: "Committed, but not assigned to a class yet" }),
      Icon: FiClock,
      tone: "bg-amber-50 text-amber-700 ring-amber-100 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900/50",
    },
    [CHILD_OVERVIEW_STATUS.NOT_COMMITTED]: {
      label: t("parentPortal.children.overview_status.not_committed", { defaultValue: "Not committed" }),
      description: t("parentPortal.children.overview_status.not_committed_hint", { defaultValue: "Child will not attend the school" }),
      Icon: FiXCircle,
      tone: "bg-rose-50 text-rose-700 ring-rose-100 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900/50",
    },
    [CHILD_OVERVIEW_STATUS.INACTIVE]: {
      label: t("parentPortal.children.overview_status.inactive", { defaultValue: "Inactive" }),
      description: t("parentPortal.children.overview_status.inactive_hint", { defaultValue: "For example, withdrawn or not started" }),
      Icon: FiMinusCircle,
      tone: "bg-slate-100 text-slate-500 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
    },
  };
  return entries[status] || entries[CHILD_OVERVIEW_STATUS.INACTIVE];
};

const ChildOverviewStatusBadge = ({ status, t, compact = false }) => {
  const { label, Icon, tone } = childOverviewStatusMeta(status, t);
  return (
    <span
      title={label}
      aria-label={label}
      className={`inline-flex items-center justify-center gap-1.5 rounded-full font-bold ring-1 ${tone} ${compact ? "h-8 w-8 p-0" : "px-3 py-1.5 text-xs"}`}
    >
      <Icon className={compact ? "h-5 w-5" : "h-4 w-4"} aria-hidden="true" />
      {!compact && <span>{label}</span>}
    </span>
  );
};

const PrivacyOverviewStatusBadge = ({ configured, loading = false, t, compact = false }) => {
  const label = loading
    ? t("common.loading", { defaultValue: "Loading…" })
    : configured
      ? t("parentPortal.children.overview_status.privacy_set", { defaultValue: "Privacy configured" })
      : t("parentPortal.children.overview_status.privacy_missing", { defaultValue: "Configure privacy" });
  const tone = configured
    ? "bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/50"
    : "bg-rose-50 text-rose-600 ring-rose-100 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900/50";
  return (
    <span
      title={label}
      aria-label={label}
      className={`inline-flex items-center justify-center gap-1.5 rounded-full font-bold ring-1 ${tone} ${compact ? "h-8 w-8 p-0" : "px-3 py-1.5 text-xs"} ${loading ? "opacity-50" : ""}`}
    >
      <FiShield className={compact ? "h-5 w-5" : "h-4 w-4"} aria-hidden="true" />
      {!compact && <span>{label}</span>}
    </span>
  );
};

const getClassroom = (child, classroomsByChild) => {
  const current = getActiveClassroomAssignment(child, classroomsByChild);
  return firstValue(
    current?.classroom?.className,
    current?.classroom?.name,
    current?.className,
    current?.classroomName,
    child?.className,
    child?.classroomName,
    child?.classroom?.className,
    child?.classroom?.name,
    child?.currentClassName,
    child?.currentClassroomName,
  ) ?? "—";
};

const getTeacherName = (child, classroomsByChild) => {
  const current = getActiveClassroomAssignment(child, classroomsByChild);
  const teacherTeamNames = Array.isArray(current?.teachers)
    ? current.teachers
        .map((teacher) => fullName(teacher?.firstName, teacher?.lastName))
        .filter(Boolean)
    : [];
  const teacherObj =
    current?.teacher ||
    (Array.isArray(current?.teachers) ? current.teachers[0] : null) ||
    current?.classroom?.primaryTeacher ||
    (Array.isArray(current?.classroom?.teachers) ? current.classroom.teachers[0] : null) ||
    child?.teacher ||
    child?.classroom?.teacher;

  return (
    (teacherTeamNames.length > 0 && teacherTeamNames.join(", ")) ||
    fullName(current?.teacherFirstName, current?.teacherLastName) ||
    fullName(teacherObj?.firstName, teacherObj?.lastName) ||
    fullName(child?.teacherFirstName, child?.teacherLastName) ||
    fullName(child?.classroom?.teacherFirstName, child?.classroom?.teacherLastName) ||
    firstValue(
      current?.teacherName,
      current?.classroom?.teacherName,
      teacherObj?.name,
      child?.teacherName,
      child?.classroom?.teacherName,
      child?.classroom?.teacher?.name,
    ) ||
    "—"
  );
};

const getTeacherContactInfo = (child, classroomsByChild) => {
  const current = getActiveClassroomAssignment(child, classroomsByChild);
  const teacherObj =
    current?.teacher ||
    (Array.isArray(current?.teachers) ? current.teachers[0] : null) ||
    current?.classroom?.primaryTeacher ||
    (Array.isArray(current?.classroom?.teachers) ? current.classroom.teachers[0] : null) ||
    child?.teacher ||
    child?.classroom?.teacher ||
    null;

  return {
    firstName: firstValue(current?.teacherFirstName, teacherObj?.firstName, child?.teacherFirstName),
    lastName: firstValue(current?.teacherLastName, teacherObj?.lastName, child?.teacherLastName),
    name: firstValue(current?.teacherName, current?.classroom?.teacherName, teacherObj?.name, child?.teacherName, child?.classroom?.teacherName),
    email: firstValue(current?.teacherEmail, teacherObj?.email, child?.teacherEmail, child?.classroom?.teacherEmail),
    phone: firstValue(current?.teacherPhone, teacherObj?.phone, child?.teacherPhone, child?.classroom?.teacherPhone),
    addressLine: firstValue(current?.teacherAddress, teacherObj?.address, teacherObj?.street, child?.teacherAddress),
    zipCode: firstValue(current?.teacherZipCode, teacherObj?.zipCode, teacherObj?.postalCode),
    city: firstValue(current?.teacherCity, teacherObj?.city),
  };
};

const getChildActivities = (child) => {
  const source = firstValue(child?.outings, child?.trips, child?.activities, child?.classroom?.outings, child?.classroom?.activities);
  return Array.isArray(source) ? source : [];
};

const getActivityTitle = (activity, t) =>
  firstValue(activity?.title, activity?.name, activity?.eventName, activity?.description) ||
  t("parentPortal.children.activity", { defaultValue: "Activity" });

const getActivityDate = (activity) =>
  formatDateValue(firstValue(activity?.date, activity?.startDate, activity?.eventDate, activity?.startTime));

const formatGender = (value, t) => {
  if (!value) return t("parentPortal.children.not_available", { defaultValue: "Not available" });

  const normalized = String(value).trim().toUpperCase();
  if (normalized === "M") return t("children.common.gender_male", { defaultValue: "Male" });
  if (normalized === "F") return t("children.common.gender_female", { defaultValue: "Female" });

  return value;
};

const FamilyInfoRow = ({ icon: Icon, label, value, action }) => (
  <div className="flex items-center gap-4 border-b border-slate-100 py-5 last:border-b-0 dark:border-slate-800">
    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
      <Icon aria-hidden="true" className="h-5 w-5" />
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{label}</p>
      <div className="mt-1 break-words text-base font-bold text-slate-950 dark:text-slate-100">{value}</div>
    </div>
    {action}
  </div>
);

const FamilyTab = ({
  t,
  family,
  familyDraft,
  setFamilyDraft,
  familyEditMode,
  setFamilyEditMode,
  hasPendingFamilyUpdate,
  hasPendingFamilySituationRequest,
  handleSaveFamilySituation,
  onSubmitFamilySituationRequest,
  parentData,
  onOpenChild,
  classroomsByChild,
  loadClassroomsForChild,
  privacyByChild,
  privacyActionChildId,
  onPrivacyActionHandled,
  bsnActionChildId,
  onBsnActionHandled,
  healthActionChildId,
  onHealthActionHandled,
  loadPrivacyFor,
  childInfoEdit = {},
  pendingChildUpdateSet = new Set(),
  startChildInfoEdit,
  updateChildInfoDraft,
  cancelChildInfoEdit,
  submitChildInfoEdit,
  startEditPrivacy,
  cancelEditPrivacy,
  savePrivacyFor,
  addMode,
  setAddMode,
  newChild = {},
  setNewChild,
  handleNewChildChange,
  handleCreateChild,
  onAddChild,
}) => {
  const [situationFormOpen, setSituationFormOpen] = useState(false);
  const [situationReason, setSituationReason] = useState("OTHER");
  const [situationNote, setSituationNote] = useState("");
  const [situationSaving, setSituationSaving] = useState(false);
  const [situationSubmitted, setSituationSubmitted] = useState(false);
  const [situationError, setSituationError] = useState("");
  const [newPartner, setNewPartner] = useState(emptyNewPartner);
  const reportsNewPartner = situationReason === NEW_PARTNER_REASON;
  const [selectedChild, setSelectedChild] = useState(null);
  const [familyViewDirection, setFamilyViewDirection] = useState("right");
  const [childDetailSection, setChildDetailSection] = useState("class");
  const [childrenActionsOpen, setChildrenActionsOpen] = useState(false);
  const [healthViewOpen, setHealthViewOpen] = useState(false);
  const [localAddChildOpen, setLocalAddChildOpen] = useState(false);
  const [addChildSubmitting, setAddChildSubmitting] = useState(false);
  const addChildSubmittingRef = useRef(false);
  const [addChildError, setAddChildError] = useState("");
  const [localPrivacyEditingChildId, setLocalPrivacyEditingChildId] = useState(null);
  const children = useMemo(
    () => (Array.isArray(family?.children) ? family.children : []),
    [family?.children],
  );

  useEffect(() => {
    children.forEach((child) => {
      const childId = getChildApiId(child) || getChildKey(child);
      if (!childId) return;

      const classroomState = classroomsByChild?.[childId];
      if (!hasLoadedStateData(classroomState) && !classroomState?.loading && !classroomState?.error) {
        loadClassroomsForChild?.(childId);
      }

      const privacyState = privacyByChild?.[childId];
      if (!hasLoadedStateData(privacyState) && !privacyState?.loading && !privacyState?.error) {
        loadPrivacyFor?.(childId);
      }
    });
  }, [children, classroomsByChild, loadClassroomsForChild, loadPrivacyFor, privacyByChild]);
  // Withdrawn children have no health profile (it is removed when they leave).
  const healthProfileChildren = useMemo(
    () => children.filter((child) => getChildApiId(child) && !child?.withdrawnAt && !child?.deceasedAt),
    [children],
  );
  const situationPending = hasPendingFamilySituationRequest || situationSubmitted;
  const hasInlineAddChildFlow =
    newChild &&
    typeof handleNewChildChange === "function" &&
    typeof handleCreateChild === "function";
  const addChildPanelOpen = hasInlineAddChildFlow && (Boolean(addMode) || localAddChildOpen);
  const canAddChild = hasInlineAddChildFlow || typeof onAddChild === "function" || typeof setAddMode === "function";

  useEffect(() => {
    if (!privacyActionChildId) return;

    const child = children.find((item) => String(getChildApiId(item) || getChildKey(item)) === String(privacyActionChildId));
    if (!child) return;

    const childApiId = getChildApiId(child) || getChildKey(child);
    setFamilyViewDirection("right");
    setSelectedChild(child);
    setChildDetailSection("privacy");
    setLocalPrivacyEditingChildId(childApiId);
    loadPrivacyFor?.(childApiId);
    loadClassroomsForChild?.(childApiId);
    onPrivacyActionHandled?.();
  }, [children, loadClassroomsForChild, loadPrivacyFor, onPrivacyActionHandled, privacyActionChildId]);

  useEffect(() => {
    if (!bsnActionChildId) return;

    const child = children.find((item) => String(getChildApiId(item) || getChildKey(item)) === String(bsnActionChildId));
    if (!child) return;

    const childApiId = getChildApiId(child) || getChildKey(child);
    setFamilyViewDirection("right");
    setSelectedChild(child);
    setChildDetailSection("personal");
    startChildInfoEdit?.(child);
    loadPrivacyFor?.(childApiId);
    loadClassroomsForChild?.(childApiId);
    onBsnActionHandled?.();
  }, [bsnActionChildId, children, loadClassroomsForChild, loadPrivacyFor, onBsnActionHandled, startChildInfoEdit]);

  useEffect(() => {
    if (!healthActionChildId) return;

    const child = children.find((item) => String(getChildApiId(item) || getChildKey(item)) === String(healthActionChildId));
    if (!child) return;

    const childApiId = getChildApiId(child) || getChildKey(child);
    setFamilyViewDirection("right");
    setSelectedChild(child);
    setChildDetailSection("health");
    loadClassroomsForChild?.(childApiId);
    onHealthActionHandled?.();
  }, [children, healthActionChildId, loadClassroomsForChild, onHealthActionHandled]);

  const selectedTeacherContact = useMemo(
    () => (selectedChild ? getTeacherContactInfo(selectedChild, classroomsByChild) : {}),
    [selectedChild, classroomsByChild],
  );
  const selectedChildApiId = useMemo(() => getChildApiId(selectedChild), [selectedChild]);


  useEffect(() => {
    if (!selectedChildApiId) return;
    loadClassroomsForChild?.(selectedChildApiId);
    loadPrivacyFor?.(selectedChildApiId);
  }, [selectedChildApiId, loadClassroomsForChild, loadPrivacyFor]);

  useEffect(() => {
    if (!selectedChild || !isWaitingListChild(selectedChild)) return;
    if (childDetailSection === "privacy" || childDetailSection === "class") {
      setChildDetailSection("personal");
    }
  }, [selectedChild, childDetailSection]);

  const handleAddChild = () => {
    setChildrenActionsOpen(false);
    if (hasInlineAddChildFlow) {
      setFamilyViewDirection("right");
      setLocalAddChildOpen(true);
      setAddMode?.(true);
      return;
    }
    if (typeof onAddChild === "function") {
      onAddChild();
      return;
    }
    if (typeof setAddMode === "function") {
      setAddMode(true);
    }
  };

  const closeAddChildPanel = () => {
    setFamilyViewDirection("left");
    setLocalAddChildOpen(false);
    setAddMode?.(false);
    setAddChildError("");
    setNewChild?.({ firstName: "", lastName: "", dateBirth: "", gender: "" });
  };

  const submitNewChild = async () => {
    if (addChildSubmittingRef.current || typeof handleCreateChild !== "function") return false;

    addChildSubmittingRef.current = true;
    setAddChildError("");
    setAddChildSubmitting(true);
    try {
      const result = await handleCreateChild();
      if (result === false) {
        setAddChildError(
          t("parentPortal.create_child_failed", {
            defaultValue: "The child registration could not be submitted. Please try again.",
          }),
        );
        return false;
      }

      setFamilyViewDirection("left");
      setLocalAddChildOpen(false);
      setAddMode?.(false);
      setAddChildError("");
      return true;
    } catch (error) {
      setAddChildError(
        error?.response?.data?.message ||
          error?.message ||
          t("parentPortal.create_child_failed", {
            defaultValue: "The child registration could not be submitted. Please try again.",
          }),
      );
      return false;
    } finally {
      addChildSubmittingRef.current = false;
      setAddChildSubmitting(false);
    }
  };

  const partnerName = useMemo(() => {
    if (!family) return "";

    const parentId = parentData?.id || parentData?.uuid;
    const parent1Name = fullName(family.parent1FirstName, family.parent1LastName);
    const parent2Name = fullName(family.parent2FirstName, family.parent2LastName);

    if (family.parent1Id && family.parent1Id !== parentId && parent1Name) return parent1Name;
    if (family.parent2Id && family.parent2Id !== parentId && parent2Name) return parent2Name;

    return t("parentPortal.no_partner", { defaultValue: "No partner linked" });
  }, [family, parentData, t]);

  const submitSituationRequest = async (event) => {
    event.preventDefault();
    if (situationSaving || situationPending) return;

    setSituationSaving(true);
    setSituationError("");

    if (reportsNewPartner && !isNewPartnerComplete(newPartner)) {
      setSituationSaving(false);
      setSituationError(t("parentPortal.newPartner.incomplete", {
        defaultValue: "Fill in the name and e-mail address of your partner.",
      }));
      return;
    }
    if (reportsNewPartner && needsSituationChoice(family?.familySituation || "MARRIED") && !newPartner.familySituation) {
      setSituationSaving(false);
      setSituationError(t("parentPortal.newPartner.situationRequired", {
        defaultValue: "Choose the situation of your current family.",
      }));
      return;
    }

    const ok = await onSubmitFamilySituationRequest?.({
      reason: situationReason,
      note: situationNote.trim(),
      partner: reportsNewPartner ? newPartner : undefined,
      currentSituation: family?.familySituation || "MARRIED",
    });

    setSituationSaving(false);

    if (ok) {
      setSituationSubmitted(true);
      setSituationFormOpen(false);
      setSituationNote("");
      setNewPartner(emptyNewPartner());
      return;
    }

    setSituationError(
      t("parentPortal.request.messages.error", {
        defaultValue: "Request failed. Please try again.",
      }),
    );
  };

  if (!family) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-6 text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
        {t("parentPortal.no_family_found", { defaultValue: "No family found." })}
      </div>
    );
  }

  const familyName = family.familyName || t("parentPortal.family", { defaultValue: "Family" });
  const currentFamilySituation = familyDraft?.familySituation || family?.familySituation || "MARRIED";
  const familySituationValue = familySituationLabel(t, currentFamilySituation);

  if (healthViewOpen && !selectedChild) {
    return (
      <SlideIn key="family-health" direction="right">
        <FamilyHealthProfileView
          t={t}
          familyChildren={healthProfileChildren}
          onBack={() => {
            setFamilyViewDirection("left");
            setHealthViewOpen(false);
          }}
        />
      </SlideIn>
    );
  }

  if (selectedChild) {
    const childName = getChildName(selectedChild);
    const selectedChildKey = getChildKey(selectedChild);
    const selectedChildApiId = getChildApiId(selectedChild);
    const selectedChildStateKey = selectedChildApiId || selectedChildKey;
    const rawBirthDate =
      selectedChild.dateBirth ||
      selectedChild.birthDate ||
      selectedChild.dateOfBirth ||
      "";
    const birthDate = formatDateValue(rawBirthDate);
    const registrationDate = formatDateValue(
      firstValue(selectedChild.dateOfRegistration, selectedChild.registrationDate, selectedChild.registeredAt),
    );
    const gender = formatGender(selectedChild.gender, t);
    const classroom = getClassroom(selectedChild, classroomsByChild);
    const classroomAssignment = getActiveClassroomAssignment(selectedChild, classroomsByChild);
    const classroomId = firstValue(
      classroomAssignment?.classroomId,
      classroomAssignment?.classroomUuid,
      classroomAssignment?.classroom?.id,
      classroomAssignment?.classroom?.uuid,
    );
    const teacherName = getTeacherName(selectedChild, classroomsByChild);
    const teacherContact = selectedTeacherContact;
    const teacherContactName =
      fullName(teacherContact.firstName, teacherContact.lastName) ||
      teacherContact.name ||
      teacherName;
    const teacherAddressLine = firstValue(teacherContact.addressLine, teacherContact.address, teacherContact.street);
    const teacherLocationLine = [firstValue(teacherContact.zipCode, teacherContact.postalCode), teacherContact.city].filter(Boolean).join(" ");
    const teacherAddress = [teacherAddressLine, teacherLocationLine].filter(Boolean).join(", ");
    const hasTeacherContactDetails = Boolean(teacherContact.email || teacherContact.phone || teacherAddress);
    const isWaitingChild = isWaitingListChild(selectedChild);
    const status = getChildStatus(selectedChild, t);
    const classSectionLabel = isWaitingChild ? t("parentPortal.children.class_disabled", { defaultValue: "Not available yet" }) : classroom;
    const bsnKnown = Boolean(selectedChild.bsn || selectedChild.socialSecurityNumber);
    const childInfoState = childInfoEdit?.[selectedChildStateKey] || {};
    const childInfoEditing = !!childInfoState.editing;
    const childInfoPending = pendingChildUpdateSet?.has?.(String(selectedChildStateKey));
    const canEditPersonalInline =
      typeof startChildInfoEdit === "function" &&
      typeof updateChildInfoDraft === "function" &&
      typeof cancelChildInfoEdit === "function" &&
      typeof submitChildInfoEdit === "function";
    const editableBsn = firstValue(selectedChild.bsn, selectedChild.socialSecurityNumber);
    const canEditBsn = !editableBsn;
    const draftFirstName = childInfoState.draftFirstName ?? selectedChild.firstName ?? "";
    const draftLastName = childInfoState.draftLastName ?? selectedChild.lastName ?? "";
    const draftBsn = childInfoState.draftBsn ?? "";
    const activities = getChildActivities(selectedChild).slice(0, 3);
    const hasActivitySource = getChildActivities(selectedChild).length > 0;
    const selectedPrivacyState = selectedChildStateKey ? privacyByChild?.[selectedChildStateKey] : null;
    const privacyLoaded = hasLoadedStateData(selectedPrivacyState);
    const loadedPrivacy = privacyLoaded ? selectedPrivacyState?.data : null;
    const privacyLoading = selectedPrivacyState?.loading || (!privacyLoaded && typeof loadPrivacyFor === "function");
    const privacyEntries = getChildPrivacyEntries(loadedPrivacy || selectedChild, t);
    const privacyEditing = !!(selectedChildStateKey && localPrivacyEditingChildId === selectedChildStateKey);
    const canEditPrivacyInline =
      typeof savePrivacyFor === "function";
    const privacyUpdatedAt = firstValue(
      loadedPrivacy?.updatedAt,
      loadedPrivacy?.updated_at,
      loadedPrivacy?.lastUpdated,
      loadedPrivacy?.lastModifiedAt,
    );
    const privacyLastUpdated = formatDateTimeValue(privacyUpdatedAt);
    const selectedHasClassroom = getClassroomAssignments(selectedChild, classroomsByChild)
      .some(isActiveClassroomAssignment);
    const selectedOverviewStatus = resolveChildOverviewStatus({
      child: selectedChild,
      hasClassroom: selectedHasClassroom,
    });
    const selectedPrivacyConfigured = hasConfiguredPrivacy(selectedPrivacyState);

    const DetailRow = ({ label, value }) => (
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-3 last:border-b-0 dark:border-slate-800">
        <span className="min-w-0 text-sm font-semibold text-slate-500 dark:text-slate-400">{label}</span>
        <span className="max-w-[60%] text-right text-sm font-bold text-slate-950 dark:text-white">{value || "—"}</span>
      </div>
    );

    const QuickAction = ({ title, description, icon: Icon, onClick, active = false, disabled = false }) => {
      const isDisabled = disabled || !onClick;
      return (
        <button
          type="button"
          onClick={onClick}
          disabled={isDisabled}
          className={`flex w-full min-w-0 items-center gap-3 rounded-2xl border px-4 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-55 ${active ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/60 dark:bg-emerald-950/20" : "border-slate-100 bg-white hover:border-emerald-200 hover:bg-emerald-50/60 disabled:hover:border-slate-100 disabled:hover:bg-white dark:border-slate-800 dark:bg-slate-950/20 dark:hover:bg-emerald-950/20"}`}
        >
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200">
            <Icon aria-hidden="true" className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-slate-950 dark:text-white">{title}</span>
            <span className="mt-0.5 block text-sm text-slate-500 dark:text-slate-400">{description}</span>
          </span>
          {!isDisabled && <FiChevronRight className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />}
        </button>
      );
    };

    return (
      <SlideIn key={`child-${selectedChildKey || "selected"}`} direction="right">
        <div className="space-y-4 text-slate-950 dark:text-slate-100">
        <section className="space-y-4">
          <div>
            <div className="flex flex-col gap-4 rounded-2xl border border-emerald-100 bg-emerald-50/35 p-5 shadow-[0_16px_42px_-34px_rgba(15,23,42,0.35)] dark:border-emerald-900/60 dark:bg-emerald-950/20 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-center gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setFamilyViewDirection("left");
                    setSelectedChild(null);
                    setChildDetailSection("class");
                    setLocalPrivacyEditingChildId(null);
                  }}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-emerald-100 bg-white text-emerald-800 shadow-sm transition hover:-translate-x-0.5 hover:border-emerald-200 hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-slate-900 dark:text-emerald-200 dark:hover:bg-emerald-950/30"
                  aria-label={t("common.back", { defaultValue: "Back" })}
                >
                  <FiArrowLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-2xl font-bold text-emerald-800 shadow-sm dark:bg-slate-900 dark:text-emerald-200">
                  {initialsFor(childName)}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
                    {t("parentPortal.children.child_profile", { defaultValue: "Child profile" })}
                  </p>
                  <h2 className="mt-1 truncate text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
                    {childName}
                  </h2>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <ChildOverviewStatusBadge status={selectedOverviewStatus} t={t} />
                <PrivacyOverviewStatusBadge
                  configured={selectedPrivacyConfigured}
                  loading={privacyLoading}
                  t={t}
                />
              </div>
            </div>

            {true ? (
              <div className="mt-5 grid items-start gap-5 xl:grid-cols-[minmax(16rem,0.42fr)_minmax(0,1.58fr)]">
                <aside className="self-start rounded-2xl border border-slate-100 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/20">
                  <h3 className="text-base font-bold text-slate-950 dark:text-white">
                    {t("parentPortal.children.profile_sections", { defaultValue: "Profile sections" })}
                  </h3>

                  <div className="mt-4 space-y-2">
                    <QuickAction
                      icon={FiShield}
                      title={t("parentPortal.children.privacy_publication", { defaultValue: "Privacy & publication" })}
                      description={
                        isWaitingChild
                          ? t("parentPortal.children.privacy_waiting_hint", { defaultValue: "Available after placement." })
                          : t("parentPortal.children.privacy_hint", { defaultValue: "Photo and publication preferences." })
                      }
                      onClick={isWaitingChild ? null : () => setChildDetailSection("privacy")}
                      active={childDetailSection === "privacy"}
                      disabled={isWaitingChild}
                    />
                    <QuickAction
                      icon={FiClipboard}
                      title={t("parentPortal.health.title", { defaultValue: "Contacts & notes" })}
                      description={t("parentPortal.health.summary_hint", {
                        defaultValue: "Doctor, emergency contact and allergies, used for outing forms.",
                      })}
                      onClick={() => setChildDetailSection("health")}
                      active={childDetailSection === "health"}
                    />
                    <QuickAction
                      icon={FiUser}
                      title={t("parentPortal.children.personal_details", { defaultValue: "Personal details" })}
                      description={t("parentPortal.children.personal_details_hint", { defaultValue: "Name, birth date and identifiers." })}
                      onClick={() => setChildDetailSection("personal")}
                      active={childDetailSection === "personal"}
                    />
                    <QuickAction
                      icon={FiUsers}
                      title={t("parentPortal.children.class_information", { defaultValue: "Class information" })}
                      description={classSectionLabel}
                      onClick={isWaitingChild ? null : () => setChildDetailSection("class")}
                      active={childDetailSection === "class"}
                      disabled={isWaitingChild}
                    />
                  </div>
                </aside>

                <section className="min-h-[34rem] rounded-2xl border border-slate-100 bg-white p-5 dark:border-slate-800 dark:bg-slate-950/20">
                  {childDetailSection === "class" && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
                        {t("parentPortal.children.class_information", { defaultValue: "Class information" })}
                      </p>
                      <h3 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                        {classSectionLabel}
                      </h3>
                      {isWaitingChild ? (
                        <div className="mt-5">
                          <InterestListNotice t={t} variant="class" />
                        </div>
                      ) : (
                        <>
                          <div className="mt-5 rounded-2xl border border-slate-100 dark:border-slate-800">
                            <DetailRow label={t("parentPortal.children.classroom", { defaultValue: "Classroom" })} value={classroom} />
                            <DetailRow label={t("parentPortal.children.teacher", { defaultValue: "Teacher" })} value={teacherContactName} />
                            <DetailRow label={t("parentPortal.children.status", { defaultValue: "Status" })} value={status} />
                          </div>

                          <TeacherContactCard
                            t={t}
                            contact={teacherContact}
                            teacherName={teacherContactName}
                          />
                          <ClassParentsSection classroomId={classroomId} embedded />
                          <ClassLinksSection schoolWide embedded allowManagement={false} />
                          <ClassLinksSection classroomId={classroomId} embedded />
                        </>
                      )}

                      {hasActivitySource && (
                        <div className="mt-5 rounded-2xl border border-slate-100 dark:border-slate-800">
                          <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                            <h4 className="text-sm font-bold text-slate-950 dark:text-white">
                              {t("parentPortal.children.activities", { defaultValue: "Activities" })}
                            </h4>
                          </div>
                          <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            {activities.map((activity, index) => (
                              <div key={activity?.id || activity?.uuid || `${getActivityTitle(activity, t)}-${index}`} className="flex items-center gap-3 px-4 py-3">
                                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200">
                                  <FiCalendar aria-hidden="true" className="h-4 w-4" />
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block text-sm font-bold text-slate-950 dark:text-white">{getActivityTitle(activity, t)}</span>
                                  <span className="block truncate text-sm text-slate-500 dark:text-slate-400">{getActivityDate(activity)}</span>
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {childDetailSection === "personal" && (
                    <div>
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
                            {t("parentPortal.children.personal_details", { defaultValue: "Personal details" })}
                          </p>
                          <h3 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                            {t("parentPortal.children.child_details", { defaultValue: "Child details" })}
                          </h3>
                        </div>

                        {!childInfoEditing && (canEditPersonalInline || typeof onOpenChild === "function") && (
                          childInfoPending ? (
                            <span className="inline-flex items-center justify-center rounded-full bg-amber-100 px-4 py-2 text-sm font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-100">
                              {t("parentPortal.child_info.pending_change", { defaultValue: "Pending change" })}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                if (canEditPersonalInline) {
                                  startChildInfoEdit(selectedChild);
                                  return;
                                }
                                onOpenChild?.(selectedChild);
                              }}
                              className="inline-flex items-center justify-center rounded-full border border-emerald-200 px-5 py-2 text-sm font-bold text-emerald-700 transition hover:bg-emerald-50 dark:border-emerald-900/60 dark:text-emerald-300 dark:hover:bg-emerald-950/30"
                            >
                              {t("common.actions.edit", { defaultValue: "Edit" })}
                            </button>
                          )
                        )}
                      </div>

                      {childInfoPending && !childInfoEditing && <PendingPersonalUpdateNotice t={t} />}

                      {childInfoEditing ? (
                        <div className="mt-5 rounded-2xl border border-slate-100 p-4 dark:border-slate-800">
                          <div className="grid gap-4 sm:grid-cols-2">
                            <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300">
                              {t("common.first_name", { defaultValue: "First name" })}
                              <input
                                type="text"
                                value={draftFirstName}
                                onChange={(event) => updateChildInfoDraft(selectedChildStateKey, "firstName", event.target.value)}
                                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-950 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 dark:border-slate-800 dark:bg-slate-950/30 dark:text-white"
                              />
                            </label>
                            <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300">
                              {t("common.last_name", { defaultValue: "Last name" })}
                              <input
                                type="text"
                                value={draftLastName}
                                onChange={(event) => updateChildInfoDraft(selectedChildStateKey, "lastName", event.target.value)}
                                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-950 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 dark:border-slate-800 dark:bg-slate-950/30 dark:text-white"
                              />
                            </label>
                          </div>

                          <div className="mt-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                            <DetailRow label={t("parentPortal.date_birth", { defaultValue: "Date of birth" })} value={birthDate} />
                            <DetailRow label={t("parentPortal.gender", { defaultValue: "Gender" })} value={gender} />
                            <DetailRow
                              label={t("parentPortal.children.bsn_status", { defaultValue: "BSN status" })}
                              value={
                                bsnKnown
                                  ? t("parentPortal.children.bsn_known", { defaultValue: "Registered" })
                                  : t("parentPortal.children.bsn_missing", { defaultValue: "Not registered" })
                              }
                            />
                          </div>

                          {!bsnKnown && (
                            <label className="mt-4 block text-sm font-semibold text-slate-600 dark:text-slate-300">
                              {t("parentPortal.children.bsn_status", { defaultValue: "BSN status" })}
                              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                                <div className="flex min-w-0 flex-1 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-950/30">
                                  <FiHash className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                                  <input
                                    type="text"
                                    value={draftBsn}
                                    onChange={(event) => updateChildInfoDraft(selectedChildStateKey, "bsn", event.target.value)}
                                    className="w-full bg-transparent text-sm font-bold text-slate-950 outline-none placeholder:text-slate-400 dark:text-white"
                                    placeholder={t("children.common.bsn", { defaultValue: "BSN" })}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => updateChildInfoDraft(selectedChildStateKey, "bsn", "-")}
                                  className={`inline-flex shrink-0 items-center justify-center rounded-2xl border px-4 py-3 text-xs font-bold transition ${
                                    draftBsn === "-"
                                      ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100"
                                      : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-300"
                                  }`}
                                >
                                  {t("parentPortal.child_info.do_not_share_bsn", { defaultValue: "Do not share BSN" })}
                                </button>
                              </div>
                            </label>
                          )}

                          {childInfoState.error && (
                            <p className="mt-3 text-sm font-semibold text-red-600 dark:text-red-400">{childInfoState.error}</p>
                          )}

                          <div className="mt-5 grid gap-3 sm:grid-cols-2">
                            <button
                              type="button"
                              onClick={() => cancelChildInfoEdit(selectedChildStateKey)}
                              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200"
                            >
                              {t("common.cancel", { defaultValue: "Cancel" })}
                            </button>
                            <button
                              type="button"
                              onClick={() => submitChildInfoEdit(selectedChild)}
                              disabled={!!childInfoState.saving}
                              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60"
                            >
                              <FiSave className="h-4 w-4" aria-hidden="true" />
                              {childInfoState.saving
                                ? t("common.saving", { defaultValue: "Saving..." })
                                : t("common.save", { defaultValue: "Save" })}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-5 overflow-hidden rounded-2xl border border-slate-100 dark:border-slate-800">
                          <DetailRow label={t("parentPortal.date_birth", { defaultValue: "Date of birth" })} value={birthDate} />
                          <DetailRow label={t("parentPortal.gender", { defaultValue: "Gender" })} value={gender} />
                          <DetailRow
                            label={t("parentPortal.children.bsn_status", { defaultValue: "BSN status" })}
                            value={
                              bsnKnown
                                ? t("parentPortal.children.bsn_known", { defaultValue: "Registered" })
                                : t("parentPortal.children.bsn_missing", { defaultValue: "Not registered" })
                            }
                          />
                          {registrationDate !== "—" && (
                            <DetailRow label={t("parentPortal.children.registration_date", { defaultValue: "Registration date" })} value={registrationDate} />
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {childDetailSection === "health" && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
                        {t("parentPortal.health.eyebrow", { defaultValue: "For outings and emergencies" })}
                      </p>
                      <h3 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                        {t("parentPortal.health.heading", { defaultValue: "Contacts & notes" })}
                      </h3>
                      <div className="mt-4">
                        <ChildHealthProfilePanel
                          t={t}
                          childId={selectedChildApiId || getChildKey(selectedChild)}
                          childName={selectedChild?.firstName}
                        />
                      </div>
                    </div>
                  )}

                  {childDetailSection === "privacy" && (
                    <div>
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
                            {t("parentPortal.children.privacy_publication", { defaultValue: "Privacy & publication" })}
                          </p>
                          <h3 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                            {t("parentPortal.children.privacy_settings", { defaultValue: "Privacy settings" })}
                          </h3>
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            {t("parentPortal.children.privacy_settings_hint", {
                              defaultValue: "Current privacy and publication settings for this child.",
                            })}
                          </p>
                        </div>

                        {!isWaitingChild && !privacyEditing && loadedPrivacy && (canEditPrivacyInline || typeof onOpenChild === "function") && (
                          <button
                            type="button"
                            onClick={async () => {
                              if (canEditPrivacyInline) {
                                setLocalPrivacyEditingChildId(selectedChildStateKey);
                                startEditPrivacy?.(selectedChildStateKey);
                                const currentPrivacyState = selectedChildStateKey ? privacyByChild?.[selectedChildStateKey] : null;
                                if (selectedChildStateKey && !hasLoadedStateData(currentPrivacyState) && !currentPrivacyState?.loading) {
                                  await loadPrivacyFor?.(selectedChildStateKey);
                                }
                                return;
                              }
                              onOpenChild?.(selectedChild);
                            }}
                            className="inline-flex shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-white px-4 py-2 text-sm font-bold text-emerald-700 transition hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-slate-950/20 dark:text-emerald-300 dark:hover:bg-emerald-950/30"
                          >
                            {t("common.actions.edit", { defaultValue: "Edit" })}
                          </button>
                        )}
                      </div>

                      <div className="mt-5 rounded-2xl border border-slate-100 dark:border-slate-800">
                        {isWaitingChild ? (
                          <div className="p-4">
                            <InterestListNotice t={t} variant="privacy" />
                          </div>
                        ) : canEditPrivacyInline && privacyEditing ? (
                          <div className="p-4">
                            <PrivacyEditor
                              t={t}
                              initial={loadedPrivacy}
                              disabled={false}
                              onCancel={() => {
                                cancelEditPrivacy?.(selectedChildStateKey);
                                setLocalPrivacyEditingChildId(null);
                              }}
                              onSave={async (payload) => {
                                await savePrivacyFor?.(selectedChildStateKey, payload);
                                setLocalPrivacyEditingChildId(null);
                              }}
                            />
                          </div>
                        ) : privacyLoading ? (
                          <div className="px-4 py-5 text-sm font-semibold text-slate-500 dark:text-slate-400">
                            {t("common.loading", { defaultValue: "Loading..." })}
                          </div>
                        ) : loadedPrivacy ? (
                          <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            {PRIVACY_CHANNELS.map(({ key, labelKey }) => (
                              <div key={key} className="flex items-center justify-between gap-4 px-4 py-3">
                                <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                                  {t(labelKey, { defaultValue: DEFAULT_CHANNEL_LABELS[key] || key })}
                                </span>
                                <PrivacyStatusPill value={loadedPrivacy?.[key]} t={t} />
                              </div>
                            ))}
                            <div className="flex items-center justify-between gap-4 px-4 py-3">
                              <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                                {t("privacy.visibility.title", { defaultValue: "Visibility" })}
                              </span>
                              <span className="text-right text-sm font-bold text-slate-950 dark:text-white">
                                {resolveVisibilityLabel(loadedPrivacy?.visibility, t)}
                              </span>
                            </div>
                            {privacyLastUpdated !== "—" && (
                              <div className="flex items-center justify-between gap-4 px-4 py-3">
                                <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                                  {t("privacy.last_updated", { defaultValue: "Last updated" })}
                                </span>
                                <span className="text-right text-sm font-bold text-slate-950 dark:text-white">
                                  {privacyLastUpdated}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="p-4">
                            <EmptyPrivacyState
                              t={t}
                              onSetPrivacy={
                                !isWaitingChild && (canEditPrivacyInline || typeof onOpenChild === "function")
                                  ? () => {
                                      if (canEditPrivacyInline) {
                                        setLocalPrivacyEditingChildId(selectedChildStateKey);
                                        startEditPrivacy?.(selectedChildStateKey);
                                        return;
                                      }
                                      onOpenChild?.(selectedChild);
                                    }
                                  : null
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              </div>
            ) : (
              <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(18rem,0.75fr)_minmax(0,1.25fr)]">
                <section className="rounded-2xl border border-slate-100 bg-white p-5 dark:border-slate-800 dark:bg-slate-950/20">
                  <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                    {t("parentPortal.children.profile_summary", { defaultValue: "Profile summary" })}
                  </h3>
                  <div className="mt-3">
                    <DetailRow label={t("parentPortal.children.classroom", { defaultValue: "Classroom" })} value={classroom} />
                    <DetailRow label={t("parentPortal.children.teacher", { defaultValue: "Teacher" })} value={teacherName} />
                    <DetailRow label={t("parentPortal.children.status", { defaultValue: "Status" })} value={status} />
                    <DetailRow label={t("parentPortal.date_birth", { defaultValue: "Date of birth" })} value={birthDate} />
                    <DetailRow label={t("parentPortal.gender", { defaultValue: "Gender" })} value={gender} />
                    <DetailRow
                      label={t("parentPortal.children.bsn_status", { defaultValue: "BSN status" })}
                      value={
                        bsnKnown
                          ? t("parentPortal.children.bsn_known", { defaultValue: "Registered" })
                          : t("parentPortal.children.bsn_missing", { defaultValue: "Not registered" })
                      }
                    />
                    {registrationDate !== "—" && (
                      <DetailRow label={t("parentPortal.children.registration_date", { defaultValue: "Registration date" })} value={registrationDate} />
                    )}
                  </div>
                </section>

                <div className="grid gap-5">
                  <section className="rounded-2xl border border-slate-100 bg-white p-5 dark:border-slate-800 dark:bg-slate-950/20">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                          {t("parentPortal.children.class_and_activities", { defaultValue: "Class & activities" })}
                        </h3>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          {t("parentPortal.children.class_and_activities_hint", {
                            defaultValue: "Useful school information for this child.",
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-950/30">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200">
                            <FiUsers aria-hidden="true" className="h-5 w-5" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                              {t("parentPortal.children.classroom", { defaultValue: "Classroom" })}
                            </p>
                            <p className="mt-0.5 truncate text-sm font-bold text-slate-950 dark:text-white">{classroom}</p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-950/30">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200">
                            <FiUser aria-hidden="true" className="h-5 w-5" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                              {t("parentPortal.children.teacher", { defaultValue: "Teacher" })}
                            </p>
                            <p className="mt-0.5 truncate text-sm font-bold text-slate-950 dark:text-white">{teacherName}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {hasActivitySource && (
                      <div className="mt-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                        <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                          <h4 className="text-sm font-bold text-slate-950 dark:text-white">
                            {t("parentPortal.children.activities", { defaultValue: "Activities" })}
                          </h4>
                        </div>
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                          {activities.map((activity, index) => (
                            <div key={activity?.id || activity?.uuid || `${getActivityTitle(activity, t)}-${index}`} className="flex items-center gap-3 px-4 py-3">
                              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200">
                                <FiCalendar aria-hidden="true" className="h-4 w-4" />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm font-bold text-slate-950 dark:text-white">{getActivityTitle(activity, t)}</span>
                                <span className="block truncate text-sm text-slate-500 dark:text-slate-400">{getActivityDate(activity)}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </section>

                  <section className="grid gap-3 md:grid-cols-3">
                    <QuickAction
                      icon={FiUsers}
                      title={t("parentPortal.children.class_information", { defaultValue: "Class information" })}
                      description={classroom}
                      onClick={() => setChildDetailSection("class")}
                    />
                    <QuickAction
                      icon={FiUser}
                      title={t("parentPortal.children.personal_details", { defaultValue: "Personal details" })}
                      description={t("parentPortal.children.personal_details_hint", { defaultValue: "Name, birth date and identifiers." })}
                      onClick={() => setChildDetailSection("personal")}
                    />
                    <QuickAction
                      icon={FiShield}
                      title={t("parentPortal.children.privacy_publication", { defaultValue: "Privacy & publication" })}
                      description={t("parentPortal.children.privacy_hint", { defaultValue: "Photo and publication preferences." })}
                      onClick={() => setChildDetailSection("privacy")}
                    />
                  </section>
                </div>
              </div>
            )}
          </div>
        </section>
        </div>
      </SlideIn>
    );
  }

  return (
    <SlideIn key={addChildPanelOpen ? "family-add-child" : "family-children-list"} direction={familyViewDirection}>
      <div className="space-y-4 text-slate-950 dark:text-slate-100">
        <header className="flex items-center gap-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white p-5 ring-1 ring-emerald-100 dark:from-emerald-950/20 dark:to-slate-950/20 dark:ring-emerald-900/50">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm" aria-hidden="true">
            <FiUsers className="h-6 w-6" />
          </span>
          <span className="min-w-0">
            <h2 className="text-2xl font-black tracking-tight text-slate-950 dark:text-slate-50">
              {t("parentPortal.family", { defaultValue: "Family" })}
            </h2>
            <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
              {t("parentPortal.family_description", {
                defaultValue: "View and manage your family details and child profiles.",
              })}
            </p>
          </span>
        </header>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,0.95fr)]">
        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_16px_42px_-34px_rgba(15,23,42,0.45)] dark:border-slate-800 dark:bg-slate-900">
          <div className="px-6 py-4">
            <FamilyInfoRow
              icon={FiUsers}
              label={t("parentPortal.family_name", { defaultValue: "Family name" })}
              value={familyName}
            />

            <div className="flex items-start gap-4 border-b border-slate-100 py-5 dark:border-slate-800">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                <FiHome aria-hidden="true" className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                  {t("parentPortal.familySituation.title", { defaultValue: "Family situation" })}
                </p>
                {familyEditMode ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <select
                      aria-label={t("parentPortal.familySituation.title", { defaultValue: "Family situation" })}
                      className="h-10 w-44 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-950 outline-none transition focus:border-emerald-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      value={currentFamilySituation}
                      onChange={(e) =>
                        setFamilyDraft((prev) => ({
                          ...prev,
                          familySituation: e.target.value,
                        }))
                      }
                      disabled={hasPendingFamilyUpdate}
                    >
                      {FAMILY_SITUATIONS.map((value) => (
                        <option key={value} value={value}>
                          {familySituationLabel(t, value)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        setFamilyDraft(family);
                        setFamilyEditMode(false);
                      }}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <FiX aria-hidden="true" />
                      {t("common.cancel", { defaultValue: "Cancel" })}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveFamilySituation}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={hasPendingFamilyUpdate}
                    >
                      <FiSave aria-hidden="true" />
                      {t("common.save", { defaultValue: "Save" })}
                    </button>
                  </div>
                ) : (
                  <div className="mt-1 text-base font-bold text-slate-950 dark:text-slate-100">{familySituationValue}</div>
                )}
              </div>
              {!familyEditMode && (
                hasPendingFamilyUpdate ? (
                  <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
                    {t("parentPortal.pending_change", { defaultValue: "Pending change" })}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setFamilyEditMode(true)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <FiEdit2 aria-hidden="true" />
                    {t("common.edit", { defaultValue: "Edit" })}
                  </button>
                )
              )}
            </div>

            <FamilyInfoRow
              icon={FiUsers}
              label={t("parentPortal.partner", { defaultValue: "Partner" })}
              value={partnerName}

            />

            <FamilyInfoRow
              icon={FiUsers}
              label={t("parentPortal.children.children", { defaultValue: "Children" })}
              value={t("parentPortal.dashboard.children_count", {
                defaultValue: "{{count}} children",
                count: children.length,
              })}
            />

            {healthProfileChildren.length > 0 && (
              <FamilyInfoRow
                icon={FiClipboard}
                label={t("parentPortal.health.heading", { defaultValue: "Contacts & notes" })}
                value={
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    {t("parentPortal.health.summary_hint", {
                      defaultValue: "Doctor, emergency contact and allergies, used for outing forms.",
                    })}
                  </span>
                }
                action={
                  <button
                    type="button"
                    onClick={() => {
                      setFamilyViewDirection("right");
                      setHealthViewOpen(true);
                    }}
                    className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <FiClipboard aria-hidden="true" />
                    {t("parentPortal.health.open_button", { defaultValue: "Contacts & notes" })}
                  </button>
                }
              />
            )}
          </div>
        </section>



        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_16px_42px_-34px_rgba(15,23,42,0.45)] dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-6 py-4 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                {t("parentPortal.children.children", { defaultValue: "Children" })}
              </h2>
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                {children.length}
              </span>
            </div>

            {canAddChild && children.length >= 4 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setChildrenActionsOpen((open) => !open)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-emerald-950/30"
                  aria-label={t("parentPortal.children.actions", { defaultValue: "Child actions" })}
                >
                  <FiMoreHorizontal aria-hidden="true" className="h-5 w-5" />
                </button>
                {childrenActionsOpen && (
                  <div className="absolute right-0 z-10 mt-2 min-w-48 rounded-2xl border border-slate-100 bg-white p-2 shadow-xl dark:border-slate-800 dark:bg-slate-900">
                    <button
                      type="button"
                      onClick={handleAddChild}
                      className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-bold text-slate-700 transition hover:bg-emerald-50 hover:text-emerald-800 dark:text-slate-200 dark:hover:bg-emerald-950/30"
                    >
                      <FiPlus aria-hidden="true" />
                      {t("parentPortal.buttons.add_child", { defaultValue: "Add child" })}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {addChildPanelOpen ? (
            <ChildAddSection
              t={t}
              newChild={newChild}
              handleNewChildChange={handleNewChildChange}
              handleCreateChild={submitNewChild}
              submitting={addChildSubmitting}
              error={addChildError}
              onCancel={closeAddChildPanel}
            />
          ) : children.length > 0 ? (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {children.map((child, index) => {
                const childName = getChildName(child);
                const childId = getChildApiId(child) || getChildKey(child);
                const privacyState = childId ? privacyByChild?.[childId] : null;
                const hasClassroom = getClassroomAssignments(child, classroomsByChild)
                  .some(isActiveClassroomAssignment);
                const overviewStatus = resolveChildOverviewStatus({ child, hasClassroom });
                const privacyConfigured = hasConfiguredPrivacy(privacyState);
                const clickable = true;
                const content = (
                  <>
                    <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${index % 5 === 0 ? "bg-amber-100 text-amber-800" : index % 5 === 1 ? "bg-emerald-100 text-emerald-800" : index % 5 === 2 ? "bg-rose-100 text-rose-800" : index % 5 === 3 ? "bg-violet-100 text-violet-800" : "bg-sky-100 text-sky-800"}`}>
                      {initialsFor(childName)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <strong className="block truncate text-base font-bold text-slate-950 dark:text-slate-100">{childName}</strong>
                      <small className="block truncate text-sm font-semibold text-slate-500 dark:text-slate-400">
                        {getChildSubtitle(child, t)}
                      </small>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <ChildOverviewStatusBadge status={overviewStatus} t={t} compact />
                      <PrivacyOverviewStatusBadge
                        configured={privacyConfigured}
                        loading={Boolean(privacyState?.loading)}
                        t={t}
                        compact
                      />
                    </span>
                    {clickable && <FiChevronRight className="h-5 w-5 text-slate-500" aria-hidden="true" />}
                  </>
                );

                return clickable ? (
                  <button
                    key={child.id || child.uuid || childName}
                    type="button"
                    onClick={() => {
                      setFamilyViewDirection("right");
                      setSelectedChild(child);
                      setChildDetailSection(isWaitingListChild(child) ? "personal" : "class");
                    }}
                    className="flex w-full items-center gap-4 px-6 py-4 text-left transition hover:bg-emerald-50/60 dark:hover:bg-emerald-950/20"
                  >
                    {content}
                  </button>
                ) : (
                  <div key={child.id || child.uuid || childName} className="flex items-center gap-4 px-6 py-4">
                    {content}
                  </div>
                );
              })}
              {canAddChild && children.length < 4 && (
                <button
                  type="button"
                  onClick={handleAddChild}
                  className="flex w-full items-center gap-4 px-6 py-4 text-left text-sm font-bold text-emerald-800 transition hover:bg-emerald-50/60 dark:text-emerald-200 dark:hover:bg-emerald-950/20"
                >
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200">
                    <FiPlus aria-hidden="true" className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    {t("parentPortal.buttons.add_child", { defaultValue: "Add child" })}
                  </span>
                  <FiChevronRight className="h-5 w-5 text-slate-500" aria-hidden="true" />
                </button>
              )}
            </div>
          ) : (
            <div className="px-6 py-5">
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                {t("parentPortal.no_children", { defaultValue: "No children found." })}
              </p>
              {canAddChild && (
                <button
                  type="button"
                  onClick={handleAddChild}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-emerald-700"
                >
                  <FiPlus aria-hidden="true" />
                  {t("parentPortal.buttons.add_child", { defaultValue: "Add child" })}
                </button>
              )}
            </div>
          )}
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_16px_42px_-34px_rgba(15,23,42,0.45)] dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-lg font-bold text-slate-950 dark:text-white">
          {t("parentPortal.children.overview_status.legend_title", { defaultValue: "Child status icons" })}
        </h2>
        <div className="mt-5 grid gap-6 lg:grid-cols-2 lg:divide-x lg:divide-slate-200 dark:lg:divide-slate-800">
          <div className="space-y-3 lg:pr-6">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">
              {t("parentPortal.children.overview_status.placement_title", { defaultValue: "Placement" })}
            </h3>
            {Object.values(CHILD_OVERVIEW_STATUS).map((overviewStatus) => {
              const meta = childOverviewStatusMeta(overviewStatus, t);
              return (
                <div key={overviewStatus} className="flex items-start gap-3">
                  <ChildOverviewStatusBadge status={overviewStatus} t={t} compact />
                  <div>
                    <p className="text-sm font-bold text-slate-950 dark:text-white">{meta.label}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{meta.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="space-y-3 lg:pl-6">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">
              {t("parentPortal.children.overview_status.privacy_title", { defaultValue: "Privacy settings" })}
            </h3>
            {[true, false].map((configured) => (
              <div key={String(configured)} className="flex items-start gap-3">
                <PrivacyOverviewStatusBadge configured={configured} t={t} compact />
                <div>
                  <p className="text-sm font-bold text-slate-950 dark:text-white">
                    {configured
                      ? t("parentPortal.children.overview_status.privacy_set", { defaultValue: "Privacy configured" })
                      : t("parentPortal.children.overview_status.privacy_missing", { defaultValue: "Configure privacy" })}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {configured
                      ? t("parentPortal.children.overview_status.privacy_set_hint", { defaultValue: "Privacy and publication preferences are complete" })
                      : t("parentPortal.children.overview_status.privacy_missing_hint", { defaultValue: "Privacy and publication preferences are missing" })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200/80 bg-white/90 p-5 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/60">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900/50">
              <FiMessageSquare className="h-6 w-6" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h3 className="text-base font-black text-slate-950 dark:text-white">
                {t("parentPortal.familySituation.detailsIncorrect", { defaultValue: "Are these details incorrect?" })}
              </h3>
              <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                {t("parentPortal.familySituation.contactHint", {
                  defaultValue: "Contact the school so we can review this together and adjust it if needed.",
                })}
              </p>
            </div>
          </div>

          {situationPending ? (
            <span className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-800 ring-1 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900/50">
              <FiCheckCircle aria-hidden="true" />
              {t("parentPortal.familySituation.pending", { defaultValue: "Your request is being processed." })}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setSituationFormOpen((open) => !open)}
              aria-expanded={situationFormOpen}
              className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-full bg-emerald-600 px-5 py-2 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-700 active:translate-y-0 dark:bg-emerald-500 dark:hover:bg-emerald-400"
            >
              {t("common.contact", { defaultValue: "Contact" })}
              <FiChevronRight
                aria-hidden="true"
                className={`transition-transform duration-300 ${situationFormOpen ? "rotate-90" : ""}`}
              />
            </button>
          )}
        </div>

        {situationFormOpen && !situationPending && (
          <div className="motion-safe:animate-[familyContactFormIn_260ms_cubic-bezier(0.22,1,0.36,1)_both]">
            <form
              className="mt-4 rounded-3xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-800 dark:bg-slate-900/40"
              onSubmit={submitSituationRequest}
            >
              <div className="grid items-start gap-4 lg:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  {t("parentPortal.familySituation.reason", { defaultValue: "Reason" })}
                  <select
                    value={situationReason}
                    onChange={(event) => setSituationReason(event.target.value)}
                    className="min-h-[46px] rounded-2xl border border-slate-200 bg-white px-4 text-base font-semibold text-slate-950 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-100 dark:focus:ring-emerald-900/30"
                  >
                    {FAMILY_SITUATION_REASONS.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {t(reason.labelKey, { defaultValue: reason.defaultValue })}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  {t("parentPortal.familySituation.note", { defaultValue: "Explanation" })}
                  <textarea
                    rows={3}
                    required={!reportsNewPartner}
                    value={situationNote}
                    onChange={(event) => setSituationNote(event.target.value)}
                    className="min-h-[112px] resize-y rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-950 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-100 dark:focus:ring-emerald-900/30"
                  />
                </label>
              </div>

              {reportsNewPartner && (
                <div className="mt-4">
                  <NewPartnerFields
                    t={t}
                    value={newPartner}
                    onChange={setNewPartner}
                    currentSituation={family?.familySituation || "MARRIED"}
                    situationOptions={FAMILY_SITUATIONS.map((value) => ({ value, label: familySituationLabel(t, value) }))}
                  />
                </div>
              )}

              {situationError && (
                <p className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-red-50 px-3 py-2 text-sm font-bold text-red-700 ring-1 ring-red-100 dark:bg-red-950/30 dark:text-red-200 dark:ring-red-900/50">
                  <FiAlertCircle aria-hidden="true" />
                  {situationError}
                </p>
              )}

              <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setSituationFormOpen(false)}
                  className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-200 dark:hover:bg-slate-900"
                >
                  <FiX aria-hidden="true" />
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="submit"
                  disabled={situationSaving}
                  className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-full bg-emerald-600 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FiMail aria-hidden="true" />
                  {situationSaving
                    ? t("common.saving", { defaultValue: "Saving..." })
                    : t("common.send", { defaultValue: "Send" })}
                </button>
              </div>
            </form>
          </div>
        )}
      </section>

      <style>{`
        @keyframes familyContactFormIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      </div>
    </SlideIn>
  );
};

export default FamilyTab;
